// Atlas V2 — Global State Store
// Manages: active workspace, instrument (provider/symbol/interval), symbols list
import React, { createContext, useContext, useReducer, useEffect, useRef } from "react";

const WORKSPACES = [
  { id: "overview",  label: "Overview" },
  { id: "markets",   label: "Markets" },
  { id: "research",  label: "Research" },
  { id: "decision",  label: "Decision Engine" },
  { id: "paper",     label: "Paper Trading" },
  { id: "futures",   label: "Futures Engine" },
  { id: "options",   label: "Options Engine" },
  { id: "models",    label: "Models" },
  { id: "features",  label: "Features" },
  { id: "system",    label: "System" },
];

const INTERVALS = ["1m","5m","15m","30m","1h","4h","1d","1wk"];

const DEFAULT_STATE = {
  workspace: "markets",
  provider: "synthetic",
  symbol: "SYN-EQUITY",
  interval: "1d",
  period: "2y",
  symbols: {
    synthetic: [
      { symbol: "SYN-EQUITY", name: "Synthetic Equity" },
      { symbol: "SYN-CRYPTO", name: "Synthetic Crypto" },
      { symbol: "SYN-FX",     name: "Synthetic FX" },
    ],
  },
  featureCount: 827,
  systemStatus: "ok",   // ok | warn | error
  cmdOpen: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "SET_WORKSPACE":  return { ...state, workspace: action.payload };
    case "SET_PROVIDER":   return { ...state, provider: action.payload };
    case "SET_SYMBOL":     return { ...state, symbol: action.payload };
    case "SET_INTERVAL":   return { ...state, interval: action.payload };
    case "SET_PERIOD":     return { ...state, period: action.payload };
    case "SET_SYMBOLS":    return { ...state, symbols: action.payload };
    case "SET_FEAT_COUNT": return { ...state, featureCount: action.payload };
    case "SET_STATUS":     return { ...state, systemStatus: action.payload };
    case "OPEN_CMD":       return { ...state, cmdOpen: true };
    case "CLOSE_CMD":      return { ...state, cmdOpen: false };
    case "SET_INSTRUMENT":
      return {
        ...state,
        provider: action.payload.provider ?? state.provider,
        symbol:   action.payload.symbol ?? state.symbol,
        interval: action.payload.interval ?? state.interval,
        period:   action.payload.period ?? state.period,
      };
    default: return state;
  }
}

const AtlasContext = createContext(null);

export function AtlasProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);

  // Keyboard shortcut: Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        dispatch({ type: "OPEN_CMD" });
      }
      if (e.key === "Escape") {
        dispatch({ type: "CLOSE_CMD" });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <AtlasContext.Provider value={{ state, dispatch, WORKSPACES, INTERVALS }}>
      {children}
    </AtlasContext.Provider>
  );
}

export function useAtlas() {
  const ctx = useContext(AtlasContext);
  if (!ctx) throw new Error("useAtlas must be inside AtlasProvider");
  return ctx;
}

export { WORKSPACES, INTERVALS };
